/**
 * Theme composable.
 *
 * Three modes:
 *  - 'system': follow prefers-color-scheme, react live to OS changes.
 *  - 'light':  force light.
 *  - 'dark':   force dark.
 *
 * Persistence (in priority order):
 *  1. server (`users.theme_preference` from /auth/me) — wins on login
 *  2. localStorage (`jatrendo.theme`) — cache between sessions / for anonymous
 *  3. 'system' as fallback default
 *
 * On change, the resolved theme is reflected on <html data-theme="…">. The
 * inline bootstrap script in index.html pre-paints the same attribute so the
 * page never flashes wrong colors on first paint.
 */
import { computed, onUnmounted, ref, watch, watchEffect } from 'vue';
import type { ResolvedTheme, ThemeMode } from '@/api/types';

const LS_KEY = 'jatrendo.theme';
const VALID_MODES: ReadonlyArray<ThemeMode> = ['system', 'light', 'dark'] as const;

function readStored(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const raw = localStorage.getItem(LS_KEY);
  return (VALID_MODES as ReadonlyArray<string>).includes(raw ?? '')
    ? (raw as ThemeMode)
    : 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Module-level singletons so all components share state without prop drilling. */
const mode = ref<ThemeMode>(readStored());
const systemDark = ref<boolean>(systemPrefersDark());
let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;
let mediaQuery: MediaQueryList | null = null;

function ensureMediaListener() {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  if (mediaListener) return;
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaListener = (e: MediaQueryListEvent) => {
    systemDark.value = e.matches;
  };
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', mediaListener);
  } else if ((mediaQuery as MediaQueryList).addListener) {
    // older browsers
    (mediaQuery as MediaQueryList).addListener(mediaListener);
  }
}

function destroyMediaListener() {
  if (!mediaQuery || !mediaListener) return;
  if (mediaQuery.removeEventListener) {
    mediaQuery.removeEventListener('change', mediaListener);
  } else if ((mediaQuery as MediaQueryList).removeListener) {
    (mediaQuery as MediaQueryList).removeListener(mediaListener);
  }
}

ensureMediaListener();

const resolved = computed<ResolvedTheme>(() => {
  if (mode.value === 'system') return systemDark.value ? 'dark' : 'light';
  return mode.value;
});

if (typeof document !== 'undefined') {
  watchEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved.value);
  });
}

function setMode(next: ThemeMode, options: { persist?: boolean } = {}) {
  const { persist = true } = options;
  if (!VALID_MODES.includes(next)) return;
  mode.value = next;
  if (persist && typeof localStorage !== 'undefined') {
    localStorage.setItem(LS_KEY, next);
  }
}

/**
 * Called by the auth store after /auth/me returns. Server value wins over
 * localStorage so a user keeps their theme across devices.
 */
function applyServerPreference(serverValue: string | null | undefined) {
  if (!serverValue) return;
  if (!(VALID_MODES as ReadonlyArray<string>).includes(serverValue)) return;
  setMode(serverValue as ThemeMode);
}

export function useTheme() {
  ensureMediaListener();
  return {
    mode,
    resolved,
    setMode,
    applyServerPreference,
    options: VALID_MODES,
  };
}

/** Test helper — resets module state between vitest specs. */
export function __resetThemeForTests() {
  destroyMediaListener();
  mediaListener = null;
  mediaQuery = null;
  mode.value = 'system';
  systemDark.value = systemPrefersDark();
  if (typeof localStorage !== 'undefined') localStorage.removeItem(LS_KEY);
  // Re-apply data-theme so the synchronous module-level watchEffect's last
  // value is reflected in the DOM (watchEffect only re-runs on dep change).
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved.value);
  }
  ensureMediaListener();
}

/** Watch hook to push changes to the backend (fire-and-forget). */
export function bindThemeServerPersistence(
  push: (mode: ThemeMode) => Promise<void> | void,
) {
  const stop = watch(mode, (next) => {
    void push(next);
  });
  onUnmounted(() => stop());
}
